import { SMALL_SCREEN_SIZE_BREAKPOINT } from 'Constants/responsiveness';
import * as React from 'react';

const MEDIA_QUERY = `(max-width: ${SMALL_SCREEN_SIZE_BREAKPOINT - 1}px)`;

const listeners = new Set<() => void>();

let mediaQueryList: MediaQueryList | null = null;

/**
 * Notifies all listeners when the media query changes.
 */
const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

/**
 * Returns the media query list.
 * @returns MediaQueryList
 */
const getMediaQueryList = (): MediaQueryList => {
  if (!mediaQueryList) {
    mediaQueryList = window.matchMedia(MEDIA_QUERY);
    mediaQueryList.addEventListener('change', notifyListeners);
  }

  return mediaQueryList;
};

/**
 * Subscribes to the media query changes.
 * @param onStoreChange - Callback function to notify when the media query changes.
 * @returns Unsubscribe function.
 */
const subscribe = (onStoreChange: () => void) => {
  getMediaQueryList();
  listeners.add(onStoreChange);

  return () => {
    listeners.delete(onStoreChange);
  };
};

/**
 * Returns the current state of the media query.
 * @returns boolean
 */
const getSnapshot = () => getMediaQueryList().matches;

/**
 * Placeholder function for server-side rendering in case we ever need it.
 * @returns boolean
 */
const getServerSnapshot = () => false;

/**
 * Tracks whether the viewport is below the small-screen breakpoint.
 * Uses a single shared matchMedia subscription for all consumers.
 */
export const useIsSmallScreen = (): boolean => {
  return React.useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};

/**
 * Clears the shared matchMedia subscription. Intended for tests only.
 */
export const resetIsSmallScreenStoreForTests = (): void => {
  if (mediaQueryList) {
    mediaQueryList.removeEventListener('change', notifyListeners);
    mediaQueryList = null;
  }

  listeners.clear();
};
